<script>
  import { createEventDispatcher } from "svelte";
  import ChangeBadge from "./ChangeBadge.svelte";
  import ChangeFilterBar from "./ChangeFilterBar.svelte";

  export let changeFilters = [];
  export let changeFilterMode = "all";
  export let selectedChangeFilters = [];
  export let changeSummary = [];
  export let filteredChangeSummary = [];
  export let diffLoading = false;
  export let diffError = "";
  export let hoveredChangeId = "";
  export let selectedChangeId = "";

  const dispatch = createEventDispatcher();

  $: visibleChangeFilters = changeFilters.filter((filter) =>
    changeSummary.some((item) => item.change === filter),
  );
</script>

<section class="change_card">
  <div class="change_toolbar">
    {#if changeSummary.length > 0}
      <ChangeFilterBar
        filters={visibleChangeFilters}
        mode={changeFilterMode}
        selectedFilters={selectedChangeFilters}
        on:all
        on:none
        on:toggle
      />
    {:else}
      <div class="change_toolbar_spacer"></div>
    {/if}
    <button
      type="button"
      class="change_close"
      aria-label="Close List of Changes"
      title="Close"
      on:click={() => dispatch("close")}
    >
      <img src="/img/assistant/icons/close_dark.svg" alt="" />
    </button>
  </div>
  <div class="table_scroll" class:scrollable={filteredChangeSummary.length > 5}>
    <table class="change_table">
      <thead>
        <tr>
          <th>#</th>
          <th>Name</th>
          <th>Type</th>
          <th>Change</th>
        </tr>
      </thead>
      <tbody>
        {#if diffLoading}
          <tr>
            <td colspan="4">Calculating changes...</td>
          </tr>
        {:else if diffError}
          <tr>
            <td colspan="4">{diffError}</td>
          </tr>
        {:else if changeSummary.length === 0}
          <tr>
            <td colspan="4">No changes detected.</td>
          </tr>
        {:else if changeFilterMode === "none"}
          <tr>
            <td colspan="4">None selected.</td>
          </tr>
        {:else if filteredChangeSummary.length === 0}
          <tr>
            <td colspan="4">No changes match the selected filters.</td>
          </tr>
        {:else}
          {#each filteredChangeSummary as item, index}
            <!-- Keep row interactions in sync with BPMN element highlighting in the parent view. -->
            <tr
              class:hovered={hoveredChangeId === item.id}
              class:selected={selectedChangeId === item.id}
              on:mouseenter={() => dispatch("hover", item.id)}
              on:mouseleave={() => dispatch("hover", "")}
              on:click={() => dispatch("select", item.id)}
            >
              <td>{index}</td>
              <td title={item.name}>{item.name}</td>
              <td>{item.type}</td>
              <td><ChangeBadge text={item.change} /></td>
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
</section>

<style>
  .change_card {
    background: var(--pax-white);
    border: var(--bw) solid var(--pax-black);
    border-radius: 0.5rem;
    bottom: 3.1rem;
    left: 0;
    max-width: calc(100% - 2rem);
    overflow: hidden;
    position: absolute;
    width: max-content;
    z-index: 2;
  }

  .change_toolbar {
    align-items: center;
    background: var(--pax-white);
    border-bottom: var(--bw) solid var(--pax-black);
    display: flex;
    justify-content: space-between;
  }

  .change_toolbar :global(.change_filters) {
    border-bottom: 0;
    flex: 1 1 auto;
  }

  .table_scroll {
    max-height: calc(2.54rem + 5 * 2.65rem);
    overflow-x: auto;
    overflow-y: hidden;
  }

  .table_scroll.scrollable {
    overflow-y: scroll;
    scrollbar-color: #8d8d8d var(--pax-smoke);
    scrollbar-gutter: stable;
    scrollbar-width: thin;
  }

  .table_scroll::-webkit-scrollbar {
    height: 0.5rem;
    width: 0.5rem;
  }

  .table_scroll::-webkit-scrollbar-track {
    background: var(--pax-smoke);
  }

  .table_scroll::-webkit-scrollbar-thumb {
    background: #8d8d8d;
    border-radius: 99px;
  }

  .table_scroll::-webkit-scrollbar-thumb:hover {
    background: #6f6f6f;
  }

  .change_toolbar_spacer {
    flex: 1 1 auto;
    min-height: 2.8rem;
  }

  .change_close {
    align-items: center;
    appearance: none;
    background: transparent;
    border: 0;
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 auto;
    height: 2.8rem;
    justify-content: center;
    padding: 0;
    width: 2.8rem;
  }

  .change_close:hover {
    background: var(--pax-smoke);
  }

  .change_close img {
    height: 0.8rem;
    width: 0.8rem;
  }

  .change_table {
    border-collapse: collapse;
    font-size: 0.875rem;
    min-width: 100%;
    table-layout: fixed;
    width: max-content;
  }

  .change_table th,
  .change_table td {
    border-bottom: var(--bw) solid var(--border-color);
    padding: 0.55rem 0.65rem;
    text-align: left;
    vertical-align: middle;
  }

  .change_table tbody tr {
    cursor: pointer;
  }

  .change_table th {
    background: var(--pax-black);
    color: var(--pax-white);
    font-weight: 400;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .change_table th:nth-child(1),
  .change_table td:nth-child(1) {
    width: 3rem;
  }

  .change_table th:nth-child(2),
  .change_table td:nth-child(2) {
    max-width: 24rem;
    min-width: 10rem;
    width: auto;
  }

  .change_table th:nth-child(3),
  .change_table td:nth-child(3) {
    width: 8rem;
  }

  .change_table th:nth-child(4),
  .change_table td:nth-child(4) {
    width: 7rem;
  }

  .change_table td {
    color: var(--pax-grey);
    transition: background-color 140ms ease-in-out;
  }

  .change_table tbody tr.hovered td {
    background: #f7f8fa;
  }

  .change_table tbody tr.selected td {
    background: var(--pax-smoke);
  }

  .change_table td:nth-child(2),
  .change_table td:nth-child(3) {
    color: var(--pax-black);
    font-weight: 400;
  }

  .change_table td:nth-child(2) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
